<template>
  <div
    :class="{
      'tw-flex tw-flex-col': orientation === 'vertical',
      'tw-flex tw-flex-row tw-items-center tw-justify-between': orientation === 'horizontal'
    }"
  >
    <div
      :class="{
        'tw-mb-1': orientation === 'vertical'
      }"
    >
      <div class="tw-flex tw-items-center">
        <label v-if="label" :for="id" class="tw-block tw-text-xs dark:tw-text-white">
          {{ label }}
        </label>

        <slot name="info" />
      </div>
      <div v-if="subLabel" class="tw-mt-1 tw-text-xs tw-text-slate-500">
        {{ subLabel }}
      </div>
      <slot name="subLabel" />
    </div>
    <div
      :class="{
        'tw-bg-white tw-p-0 dark:tw-bg-black dark:tw-text-white': true,
        'tw-w-full': orientation === 'vertical',
        '': orientation === 'horizontal',
        'tw-rounded-md': size === 'regular',
        'tw-rounded': size === 'small',
        'tw-cursor-not-allowed': disabled,
        'tw-cursor-pointer focus-within:tw-ring-deep-orange-500 hover:tw-border-deep-orange-500 focus:tw-border-deep-orange-500':
          !disabled,
        'tw-border': !showError
      }"
    >
      <select
        :id="id"
        :data-testid="id"
        :name="id"
        :value="value"
        :title="disabledReason"
        class="tw-m-0 tw-cursor-pointer"
        :class="{
          'tw-rounded-md tw-py-2 tw-pl-3 tw-pr-10 tw-text-sm': size === 'regular',
          'tw-rounded tw-py-1 tw-pl-2 tw-pr-10 tw-text-xs': size === 'small',
          'tw-cursor-pointer': !disabled,
          '!tw-cursor-not-allowed': disabled,
          '!tw-border-red-600 focus:tw-border-red-400 focus:tw-ring-red-400 dark:!tw-border-red-400': showError,
          'tw-border-none tw-outline-none': !showError
        }"
        :disabled="disabled"
        @change="$emit('change', $event)"
      >
        <option disabled value="">{{ placeholder }}</option>
        <option v-for="option in options" :key="option.value" :value="option.value" :selected="option.value === value">
          {{ option.label }}
        </option>
      </select>
    </div>
  </div>
</template>

<script lang="ts">
import { PropType } from 'vue';

export type Option = {
  value: string | number;
  label: string;
};

export default {
  name: 'StudioSelect',
  props: {
    id: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    subLabel: {
      type: String,
      default: ''
    },
    value: {
      type: [String, Number],
      default: ''
    },
    options: {
      type: Array as PropType<Option[]>,
      default: () => [],
      validator: (options: Option[]) => {
        return options.every(option => {
          return option.value && option.label;
        });
      }
    },
    size: {
      type: String,
      default: 'regular',
      validator: (size: string) => {
        return ['small', 'regular'].includes(size);
      }
    },
    orientation: {
      type: String,
      default: 'vertical',
      validator: (orientation: string) => {
        return ['horizontal', 'vertical'].includes(orientation);
      }
    },
    disabled: {
      type: Boolean,
      default: false
    },
    disabledReason: {
      type: String,
      default: ''
    },
    showError: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: 'Select an option'
    }
  }
};
</script>

<style lang="scss">
select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
  padding-right: 2.5rem;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
</style>
